/**
 * useVerifyDossier — Mutation hook that calls the `verify-dossier`
 * Edge Function to re-validate a dossier signature against live storage.
 *
 * Exposes a fine-grained status machine for the CryptoAuditCard:
 *  - idle      → not yet run
 *  - verifying → request in flight
 *  - valid     → signature OK & all files match
 *  - invalid   → signature broken or files tampered
 *  - error     → network/edge function failure
 */
import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type VerifyStatus = "idle" | "verifying" | "valid" | "invalid" | "error";

export interface VerifyFileReport {
  document_id?: string;
  storage_path?: string;
  zip_path?: string;
  expected_sha?: string;
  live_sha?: string;
  sha_match: boolean;
}

export interface VerifyResult {
  valid: boolean;
  signature_match?: boolean;
  manifest_internal_root_match?: boolean;
  live_root_matches_manifest_root?: boolean;
  files?: VerifyFileReport[];
  tampered_files?: VerifyFileReport[];
  message?: string;
  signed_at?: string;
  key_id?: string;
}

export function useVerifyDossier() {
  const [status, setStatus] = useState<VerifyStatus>("idle");
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const verify = useCallback(async (signatureId: string) => {
    setStatus("verifying");
    setError(null);
    setResult(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke("verify-dossier", {
        body: { signature_id: signatureId },
      });
      if (fnErr) throw fnErr;
      const payload = (data ?? {}) as VerifyResult;

      // Normalize tampered list from per-file report if absent
      if (!payload.tampered_files && Array.isArray(payload.files)) {
        payload.tampered_files = payload.files.filter((f) => !f.sha_match);
      }

      setResult(payload);
      setStatus(payload.valid ? "valid" : "invalid");
      return payload;
    } catch (err: any) {
      const msg = err?.message ?? "No se pudo verificar la firma";
      setError(msg);
      setStatus("error");
      throw err;
    }
  }, []);

  const reset = useCallback(() => {
    setStatus("idle");
    setResult(null);
    setError(null);
  }, []);

  return {
    status,
    result,
    error,
    verify,
    reset,
    isVerifying: status === "verifying",
  };
}
